import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { DataTable } from '../../components/ui/table';
import { PageHeader } from '../../components/ui/header';
import { PrimaryDialog } from '../../components/ui/dialog';
import WorkspaceHeaderActions from './WorkspaceHeaderActions';
import OnboardingTour from '../../components/OnboardingTour';
import { fetchProjects, deleteProjectThunk } from '../../state/slices/projectSlice';
import { fetchFlowExecutionHistoryThunk } from '../../state/slices/dashboardSlice';
import { fetchWorkflows, deleteWorkflow } from '../../state/slices/workflowSlice';
import { updateProjectStarring } from '../../apis/Projects';
import dayjs from 'dayjs';
import starIcon from '../assets/images/starIcon.png';
import folderIcon1 from '../assets/images/folderIcon1.png';
import folderIcon2 from '../assets/images/folderIcon2.png';
import recentIcon from '../assets/images/mdi_recent.png';
import solarStarIcon from '../assets/images/solar_star.png';
import borderAllIcon from '../assets/images/border-all.png';
import subcard1 from '../assets/images/subcard1.png';
import subcard2 from '../assets/images/subcard2.png';
import subcard3 from '../assets/images/subcard3.png';
import subcard4 from '../assets/images/subcard4.png';
import subcard5 from '../assets/images/subcard5.png';
import shareIcon from '../assets/images/share.png';
import deleteIcon from '../assets/images/delete.png';
import pencilIcon from '../assets/images/pencil.png';

const getStatusColor = (status) => {
  const normalizedStatus = (status || '').toLowerCase();
  switch (normalizedStatus) {
    case 'success':
    case 'completed':
      return { bgcolor: '#ecfdf5', color: '#059669', borderColor: '#059669' };
    case 'partial_success':
    case 'partial success':
    case 'warning':
      return { bgcolor: '#fffbeb', color: '#d97706', borderColor: '#d97706' };
    case 'failed':
    case 'error':
      return { bgcolor: '#fff0f0', color: '#ff5e5e', borderColor: '#ff5e5e' };
    case 'running':
    case 'active':
    case 'in_progress':
      return { bgcolor: '#eff6ff', color: '#2563eb', borderColor: '#2563eb' };
    default:
      return { bgcolor: '#f0f0f0', color: '#666', borderColor: '#ccc' };
  }
};

export default function WorkspaceContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [starUpdating, setStarUpdating] = useState(() => new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteWorkflowDialogOpen, setDeleteWorkflowDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const requestedProjectsRef = useRef(false);
  const requestedWorkflowsRef = useRef(false);
  const requestedExecutionsRef = useRef(false);
  
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { list: projectsList, loading: projectsLoading } = useSelector(
    (state) => state.projects,
  );
  const { list: workflows } = useSelector((state) => state.workflows);
  const { flowExecutionData, flowExecutionLoading } = useSelector(
    (state) => state.dashboard,
  );

  const favouriteProjectIds = useMemo(() => {
    if (!projectsList || projectsList.length === 0) return [];
    return projectsList
      .filter((p) => p.is_starred === true)
      .map((p) => p.project_id);
  }, [projectsList]);

  useEffect(() => {
    if (!requestedProjectsRef.current) {
      requestedProjectsRef.current = true;
      dispatch(fetchProjects())
        .unwrap()
        .then((projects) => {
          if (projects.length > 0 && !selectedProject) {
            const sorted = [...projects].sort((a, b) => {
              const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
              const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
              return bTime - aTime;
            });
            setSelectedProject(sorted[0]);
          }
        })
        .catch(() => toast.error('Failed to fetch projects.'));
    }
  }, [dispatch, selectedProject]);

  useEffect(() => {
    if (!requestedWorkflowsRef.current) {
      requestedWorkflowsRef.current = true;
      dispatch(fetchWorkflows()).catch(() =>
        toast.error('Failed to fetch workflows.'),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedProject) {
      dispatch(fetchFlowExecutionHistoryThunk({ limit: 1000 })).catch(() =>
        toast.error('Failed to fetch execution history.'),
      );
    }
  }, [dispatch, selectedProject?.project_id]);

  const groupProjectsByTime = (projects) => {
    const now = dayjs();
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      lastWeek: [],
      lastMonth: [],
      twoThreeMonths: [],
      threeSixMonths: [],
      sixTwelveMonths: [],
      older: [],
    };

    projects.forEach((project) => {
      const date = dayjs(project.updated_at || project.created_at);
      const diffDays = now.diff(date, 'day');
      const diffWeeks = now.diff(date, 'week');
      const diffMonths = now.diff(date, 'month');

      if (diffDays === 0) {
        groups.today.push(project);
      } else if (diffDays === 1) {
        groups.yesterday.push(project);
      } else if (diffDays < 7) {
        groups.thisWeek.push(project);
      } else if (diffWeeks < 2) {
        groups.lastWeek.push(project);
      } else if (diffMonths < 2) {
        groups.lastMonth.push(project);
      } else if (diffMonths < 3) {
        groups.twoThreeMonths.push(project);
      } else if (diffMonths < 6) {
        groups.threeSixMonths.push(project);
      } else if (diffMonths < 12) {
        groups.sixTwelveMonths.push(project);
      } else {
        groups.older.push(project);
      }
    });

    return groups;
  };

  const filteredProjects = useMemo(() => {
    if (!projectsList || projectsList.length === 0) return [];
    
    let filtered = [...projectsList];
    
    if (tab === 1) {
      filtered = filtered.filter((p) => favouriteProjectIds.includes(p.project_id));
    }
    
    return filtered.sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [projectsList, tab, favouriteProjectIds]);

  const projectExecutions = useMemo(() => {
    if (!selectedProject || !flowExecutionData || flowExecutionData.length === 0) {
      return [];
    }
    
    const projectWorkflowIds = workflows
      .filter((wf) => wf.project_id === selectedProject.project_id)
      .map((wf) => wf.workflow_id);
    
    return flowExecutionData
      .filter((exec) => projectWorkflowIds.includes(exec.workflow_id))
      .map((exec) => ({
        id: exec.workflow_id || exec.execution_id || exec.id,
        workflowId: exec.workflow_id,
        workflowName: exec.workflow_name || exec.name || 'Unknown',
        pipelineName: exec.workflow_name || exec.name || exec.workflow_name || 'Unknown',
        status: exec.run_status || exec.status || exec.execution_status || 'Unknown',
        runMode: exec.run_mode || exec.mode || 'ON DEMAND',
        lastRun: exec.last_run_at || exec.created_at || exec.run_at || '',
        source: exec.source || exec.source_name || '-',
        destination: exec.destination || exec.destination_name || '-',
      }))
      .sort((a, b) => {
        const aTime = new Date(a.lastRun || 0).getTime();
        const bTime = new Date(b.lastRun || 0).getTime();
        return bTime - aTime;
      });
  }, [selectedProject, flowExecutionData, workflows]);

  const projectStats = useMemo(() => {
    if (!selectedProject) return null;
    
    const projectWorkflows = workflows.filter(
      (wf) => wf.project_id === selectedProject.project_id,
    );
    
    return {
      workflows: projectWorkflows.length,
      transformations: 0,
      sources: 0,
      destinations: 0,
      lastRun: projectExecutions.length > 0 
        ? projectExecutions[0].status 
        : 'No runs yet',
    };
  }, [selectedProject, workflows, projectExecutions]);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    requestedExecutionsRef.current = false;
  };

  const handleDeleteProject = (projectId, projectName, e) => {
    e?.stopPropagation();
    if (!projectId) {
      toast.error('Project ID not found');
      return;
    }
    setProjectToDelete({ id: projectId, name: projectName });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      await dispatch(deleteProjectThunk(projectToDelete.id)).unwrap();
      toast.success('Project deleted successfully');
      
      if (selectedProject?.project_id === projectToDelete.id) {
        setSelectedProject(null);
      }
      
      dispatch(fetchProjects())
        .unwrap()
        .then((projects) => {
          if (projects.length > 0 && !selectedProject) {
            const sorted = [...projects].sort((a, b) => {
              const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
              const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
              return bTime - aTime;
            });
            setSelectedProject(sorted[0]);
          }
        })
        .catch(() => {});
      
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      let errorMessage = 'Failed to delete project. Please try again or contact support if the issue persists.';
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.detail) {
        errorMessage = error.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      console.error('Delete project error:', error);
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleToggleProjectStar = async (projectId, currentStarred, e) => {
    e?.stopPropagation();
    if (!projectId) {
      toast.error('Project ID not found');
      return;
    }

    const id = String(projectId);
    const nextStarred = !currentStarred;
    
    try {
      setStarUpdating((prev) => {
        const newSet = new Set(prev);
        newSet.add(id);
        return newSet;
      });
      
      await updateProjectStarring(projectId, nextStarred);
      await dispatch(fetchProjects());
      
      toast.success(
        nextStarred ? 'Project marked as favourite' : 'Project removed from favourites',
        { autoClose: 1500 },
      );
    } catch (error) {
      console.error('Star toggle failed', error?.response?.data || error?.message);
      toast.error(error?.response?.data?.detail ?? 'Failed to update favourite status');
    } finally {
      setStarUpdating((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const isStarUpdating = (projectId) => starUpdating.has(String(projectId));

  const renderProjectCard = (project, isSelected, isFavourite, dateLabel, cardWidth = 149, cardHeight = 62) => {
    const isTiles = cardWidth === 200;
    const responsiveWidth = isTiles 
      ? { xs: 160, sm: 180, md: 200 }
      : { xs: Math.min(cardWidth * 0.85, 130), sm: cardWidth };
    const responsiveHeight = isTiles
      ? { xs: 80, sm: 90, md: 100 }
      : { xs: Math.min(cardHeight * 0.9, 56), sm: cardHeight };
    
    return (
      <Card
        key={project.project_id}
        onClick={() => handleProjectSelect(project)}
        sx={{
          width: responsiveWidth,
          minWidth: responsiveWidth,
          height: responsiveHeight,
          border: isSelected ? '1px solid #2b614c' : '1px solid #e6e6e6',
          boxShadow: isSelected
            ? '0px 0px 4px 0px #3B95621A'
            : '0px 0px 4px 0px rgba(0,0,0,0.05)',
          borderRadius: '2px',
          position: 'relative',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: isSelected ? '#2b614c' : '#d0d0d0',
            boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.1)',
            '& .delete-button': {
              opacity: 1,
              visibility: 'visible',
            },
            ...(!isFavourite && {
              '& .star-button': {
                opacity: 1,
                visibility: 'visible',
              },
            }),
          },
        }}
      >
        <CardContent sx={{ p: { xs: '0.5rem', sm: '0.75rem' }, '&:last-child': { pb: { xs: '0.5rem', sm: '0.75rem' } } }}>
          <Box sx={{ display: 'flex', gap: { xs: '0.5rem', sm: '0.625rem' }, alignItems: 'flex-start' }}>
            <Box
              component="img"
              src={folderIcon1}
              alt="Project folder"
              sx={{ width: { xs: 20, sm: 24 }, height: { xs: 17, sm: 20.5 }, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0, pr: { xs: '1rem', sm: '1.8rem' }, overflow: 'hidden' }}>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
                  letterSpacing: '0%',
                  color: '#000000',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {project.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '0.5625rem', sm: '0.625rem' },
                  lineHeight: 1.2,
                  letterSpacing: '0%',
                  color: '#525252',
                  mt: { xs: '0.25rem', sm: '0.375rem' },
                }}
              >
                {dateLabel}
              </Typography>
            </Box>
          </Box>
        </CardContent>
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 2, sm: 4 },
            right: { xs: 2, sm: 4 },
            display: 'flex',
            gap: '0.25rem',
            alignItems: 'center',
          }}
        >
          <IconButton
            className="delete-button"
            size="small"
            onClick={(e) => handleDeleteProject(project.project_id, project.name, e)}
            sx={{
              p: { xs: '0.125rem', sm: '0.25rem' },
              minWidth: 'auto',
              width: { xs: 16, sm: 18 },
              height: { xs: 16, sm: 18 },
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s ease, visibility 0.2s ease',
              '&:hover': { bgcolor: 'rgba(255,0,0,0.1)' },
            }}
            title="Delete project"
          >
            <Box
              component="img"
              src={deleteIcon}
              alt="Delete"
              sx={{ width: { xs: 12, sm: 14 }, height: { xs: 12, sm: 14 } }}
            />
          </IconButton>
          <IconButton
            className="star-button"
            size="small"
            onClick={(e) => handleToggleProjectStar(project.project_id, isFavourite, e)}
            disabled={isStarUpdating(project.project_id)}
            sx={{
              p: { xs: '0.125rem', sm: '0.25rem' },
              minWidth: 'auto',
              width: { xs: 16, sm: 18 },
              height: { xs: 16, sm: 18 },
              opacity: isFavourite ? 1 : 0,
              visibility: isFavourite ? 'visible' : 'hidden',
              transition: 'opacity 0.2s ease, visibility 0.2s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            }}
            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          >
            <Box
              component="img"
              src={starIcon}
              alt={isFavourite ? 'Favourite' : 'Not favourite'}
              sx={{
                width: { xs: 12, sm: 14 },
                height: { xs: 12, sm: 14 },
                opacity: isFavourite ? 1 : 0.5,
                filter: isFavourite ? 'none' : 'grayscale(100%)',
                transition: 'all 0.2s ease',
              }}
            />
          </IconButton>
        </Box>
      </Card>
    );
  };

  const renderProjectListItem = (project, isSelected, isFavourite) => {
    return (
      <Box
        key={project.project_id}
        onClick={() => handleProjectSelect(project)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '28px',
          px: '0.5rem',
          cursor: 'pointer',
          bgcolor: isSelected ? '#e3f2fd' : 'transparent',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5',
            '& .delete-button': {
              opacity: 1,
              visibility: 'visible',
            },
            ...(!isFavourite && {
              '& .star-button': {
                opacity: 1,
                visibility: 'visible',
              },
            }),
          },
        }}
      >
        <Box
          component="img"
          src={folderIcon1}
          alt="Project folder"
          sx={{ width: 16, height: 14, mr: '0.5rem', flexShrink: 0 }}
        />
        <Typography
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 400,
            fontSize: '0.8125rem',
            color: '#000000',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {project.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: '0.25rem', alignItems: 'center', ml: '0.5rem' }}>
          <IconButton
            className="star-button"
            size="small"
            onClick={(e) => handleToggleProjectStar(project.project_id, isFavourite, e)}
            disabled={isStarUpdating(project.project_id)}
            sx={{
              p: '0.125rem',
              minWidth: 'auto',
              width: 16,
              height: 16,
              opacity: isFavourite ? 1 : 0,
              visibility: isFavourite ? 'visible' : 'hidden',
              transition: 'opacity 0.2s ease, visibility 0.2s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            }}
            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          >
            <Box
              component="img"
              src={starIcon}
              alt={isFavourite ? 'Favourite' : 'Not favourite'}
              sx={{
                width: 12,
                height: 12,
                opacity: isFavourite ? 1 : 0.5,
                filter: isFavourite ? 'none' : 'grayscale(100%)',
                transition: 'all 0.2s ease',
              }}
            />
          </IconButton>
          <IconButton
            className="delete-button"
            size="small"
            onClick={(e) => handleDeleteProject(project.project_id, project.name, e)}
            sx={{
              p: '0.125rem',
              minWidth: 'auto',
              width: 16,
              height: 16,
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s ease, visibility 0.2s ease',
              '&:hover': { bgcolor: 'rgba(255,0,0,0.1)' },
            }}
            title="Delete project"
          >
            <Box
              component="img"
              src={deleteIcon}
              alt="Delete"
              sx={{ width: 12, height: 12 }}
            />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderTimeGroupedList = () => {
    const groupedProjects = groupProjectsByTime(filteredProjects);
    const timeGroups = [
      { key: 'today', label: 'Today', projects: groupedProjects.today },
      { key: 'yesterday', label: 'Yesterday', projects: groupedProjects.yesterday },
      { key: 'thisWeek', label: 'Earlier this week', projects: groupedProjects.thisWeek },
      { key: 'lastWeek', label: 'Last week', projects: groupedProjects.lastWeek },
      { key: 'lastMonth', label: 'Last month', projects: groupedProjects.lastMonth },
      { key: 'twoThreeMonths', label: '2-3 months ago', projects: groupedProjects.twoThreeMonths },
      { key: 'threeSixMonths', label: '3-6 months ago', projects: groupedProjects.threeSixMonths },
      { key: 'sixTwelveMonths', label: '6-12 months ago', projects: groupedProjects.sixTwelveMonths },
      { key: 'older', label: 'Older', projects: groupedProjects.older },
    ].filter((group) => group.projects.length > 0);

    if (projectsLoading) {
      return (
        <Box sx={{ display: 'flex', gap: '1rem', overflowX: 'auto', pb: '1rem' }}>
          {[...Array(3)].map((_, index) => (
            <Box key={index} sx={{ minWidth: '200px', bgcolor: 'white', border: '1px solid #e6e6e6', borderRadius: '2px' }}>
              <Skeleton variant="text" width="60%" height={24} sx={{ m: '0.5rem' }} />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" width="100%" height={28} sx={{ mb: '0.25rem' }} />
              ))}
            </Box>
          ))}
        </Box>
      );
    }

    if (timeGroups.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: '2rem' }}>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#525252' }}>
            No projects found
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: { xs: '0.5rem', sm: '1rem' },
          overflowX: 'auto',
          pb: '1rem',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: '2px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d0d0d0',
            borderRadius: '1px',
            '&:hover': {
              background: '#b0b0b0',
            },
          },
        }}
      >
        {timeGroups.map((group) => (
          <Box
            key={group.key}
            sx={{
              minWidth: { xs: '160px', sm: '200px' },
              maxWidth: { xs: '180px', sm: '250px' },
              bgcolor: 'white',
              border: '1px solid #e6e6e6',
              borderRadius: '2px',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: { xs: '400px', sm: '500px' },
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '2px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#d0d0d0',
                borderRadius: '1px',
                '&:hover': {
                  background: '#b0b0b0',
                },
              },
            }}
          >
            <Box
              sx={{
                px: { xs: '0.5rem', sm: '0.75rem' },
                py: { xs: '0.375rem', sm: '0.5rem' },
                borderBottom: '1px solid #e6e6e6',
                bgcolor: '#f9f9f9',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  color: '#383838',
                  textTransform: 'uppercase',
                }}
              >
                {group.label} ({group.projects.length})
              </Typography>
            </Box>
            <Box>
              {group.projects.map((project) => {
                const isSelected = selectedProject?.project_id === project.project_id;
                const isFavourite = favouriteProjectIds.includes(project.project_id);
                return renderProjectListItem(project, isSelected, isFavourite);
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderProjects = () => {
    if (projectsLoading) {
      const skeletonCount = viewMode === 'tiles' ? 8 : 5;
      return [...Array(skeletonCount)].map((_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          width={viewMode === 'tiles' ? 200 : 149}
          height={viewMode === 'tiles' ? 100 : 62}
          sx={{ borderRadius: '2px' }}
        />
      ));
    }

    return filteredProjects.map((project) => {
      const isSelected = selectedProject?.project_id === project.project_id;
      const isFavourite = favouriteProjectIds.includes(project.project_id);
      const dateStr = project.updated_at || project.created_at;
      const dateLabel = dateStr
        ? dayjs(dateStr).isSame(dayjs(), 'day')
          ? 'Modified today'
          : dayjs(dateStr).isSame(dayjs().subtract(1, 'day'), 'day')
          ? 'Modified yesterday'
          : `Modified on ${dayjs(dateStr).format('DD MMM')}`
        : '';

      if (viewMode === 'tiles') {
        return renderProjectCard(project, isSelected, isFavourite, dateLabel, 200, 100);
      } else if (viewMode === 'details') {
        const createdDateLabel = project.created_at
          ? dayjs(project.created_at).format('DD MMM YYYY')
          : 'N/A';
        const modifiedDateLabel = project.updated_at
          ? dayjs(project.updated_at).format('DD MMM YYYY')
          : 'N/A';
        const description = project.description || '-';
        
        return (
          <Box
            key={project.project_id}
            onClick={() => handleProjectSelect(project)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: { xs: '0.5rem', sm: '0.75rem' },
              borderBottom: '1px solid #e6e6e6',
              cursor: 'pointer',
              bgcolor: isSelected ? '#f0f9f4' : 'transparent',
              '&:hover': {
                bgcolor: '#f9f9f9',
                '& .delete-button': {
                  opacity: 1,
                  visibility: 'visible',
                },
                ...(!isFavourite && {
                  '& .star-button': {
                    opacity: 1,
                    visibility: 'visible',
                  },
                }),
              },
              overflowX: { xs: 'auto', sm: 'visible' },
            }}
          >
            <Box
              component="img"
              src={folderIcon1}
              alt="Project folder"
              sx={{ width: { xs: 20, sm: 24 }, height: { xs: 17, sm: 20.5 }, mr: { xs: '0.5rem', sm: '1rem' }, flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: '#000000',
                width: { xs: '120px', sm: '200px' },
                minWidth: { xs: '120px', sm: '200px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {project.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                color: '#525252',
                flex: 1,
                minWidth: 0,
                mx: { xs: '0.5rem', sm: '1rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'block' },
              }}
              title={description}
            >
              {description}
            </Typography>
            <Box
              sx={{
                width: { xs: '100px', sm: '140px' },
                minWidth: { xs: '100px', sm: '140px' },
                maxWidth: { xs: '100px', sm: '140px' },
                textAlign: 'left',
                mr: { xs: '0.5rem', sm: '1rem' },
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  color: '#525252',
                }}
              >
                {createdDateLabel}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '100px', sm: '140px' },
                minWidth: { xs: '100px', sm: '140px' },
                maxWidth: { xs: '100px', sm: '140px' },
                textAlign: 'left',
                mr: { xs: '0.5rem', sm: '1rem' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  color: '#525252',
                }}
              >
                {modifiedDateLabel}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexShrink: 0,
                mr: { xs: '0.25rem', sm: '0.5rem' },
              }}
            >
              <IconButton
                className="star-button"
                size="small"
                onClick={(e) => handleToggleProjectStar(project.project_id, isFavourite, e)}
                disabled={isStarUpdating(project.project_id)}
                sx={{
                  p: '0.25rem',
                  minWidth: 'auto',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  opacity: isFavourite ? 1 : 0,
                  visibility: isFavourite ? 'visible' : 'hidden',
                  transition: 'opacity 0.2s ease, visibility 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                }}
                title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
              >
                <Box
                  component="img"
                  src={starIcon}
                  alt={isFavourite ? 'Favourite' : 'Not favourite'}
                  sx={{
                    width: { xs: 12, sm: 14 },
                    height: { xs: 12, sm: 14 },
                    opacity: isFavourite ? 1 : 0.5,
                    filter: isFavourite ? 'none' : 'grayscale(100%)',
                    transition: 'all 0.2s ease',
                  }}
                />
              </IconButton>
              <IconButton
                className="delete-button"
                size="small"
                onClick={(e) => handleDeleteProject(project.project_id, project.name, e)}
                sx={{
                  p: '0.25rem',
                  minWidth: 'auto',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  opacity: 0,
                  visibility: 'hidden',
                  transition: 'opacity 0.2s ease, visibility 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(255,0,0,0.1)' },
                }}
                title="Delete project"
              >
                <Box
                  component="img"
                  src={deleteIcon}
                  alt="Delete"
                  sx={{ width: { xs: 12, sm: 14 }, height: { xs: 12, sm: 14 } }}
                />
              </IconButton>
            </Box>
          </Box>
        );
      } else {
        return renderProjectCard(project, isSelected, isFavourite, dateLabel);
      }
    });
  };

  const handlePipelineNameClick = (row) => {
    if (row.workflowId) {
      navigate(`/parentworkflow/${row.workflowId}`);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'pipelineName',
      label: 'Pipeline Name',
      sortable: false,
      render: (row) => (
        <Box
          component="span"
          onClick={() => handlePipelineNameClick(row)}
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '0.8125rem',
            lineHeight: 1.2,
            letterSpacing: '0%',
            verticalAlign: 'middle',
            textDecoration: 'underline',
            textDecorationStyle: 'solid',
            textDecorationThickness: '0%',
            textDecorationOffset: '0%',
            color: '#525252',
            cursor: 'pointer',
            '&:hover': { color: '#3b9562' },
          }}
        >
          {row.pipelineName}
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Execution Status',
      sortable: false,
      render: (row) => (
        <Chip
          label={row.status}
          size="small"
          sx={{
            ...getStatusColor(row.status),
            border: `1px solid ${getStatusColor(row.status).borderColor}`,
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '0.6875rem',
            lineHeight: '1.125rem',
            letterSpacing: '0%',
            textAlign: 'center',
            height: 22,
            textTransform: 'capitalize',
          }}
        />
      ),
    },
    {
      key: 'runMode',
      label: 'Run Mode',
      sortable: false,
      cellSx: {
        fontFamily: '"Inter", sans-serif',
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.2,
        letterSpacing: '0%',
        verticalAlign: 'middle',
        color: '#0F172A',
      },
    },
    {
      key: 'lastRun',
      label: 'Last Run At',
      sortable: false,
      cellSx: {
        fontFamily: '"Inter", sans-serif',
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: 1.2,
        letterSpacing: '0%',
        verticalAlign: 'middle',
        color: '#525252',
      },
    },
    {
      key: 'source',
      label: 'Source',
      sortable: false,
      render: () => '-',
      cellSx: {
        fontSize: '0.75rem',
        color: '#525252',
      },
    },
    {
      key: 'destination',
      label: 'Destination',
      sortable: false,
      render: () => '-',
      cellSx: {
        fontSize: '0.75rem',
        color: '#525252',
      },
    },
  ], []);

  const handleShare = async (row) => {
    try {
      const workflowUrl = `${window.location.origin}/parentworkflow/${row.workflowName}`;
      await navigator.clipboard.writeText(workflowUrl);
      toast.success('Workflow link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link to clipboard');
    }
  };

  const handleDelete = (row, e) => {
    e?.stopPropagation();
    if (!row.workflowId) {
      toast.error('Workflow ID not found');
      return;
    }
    setWorkflowToDelete({ id: row.workflowId, name: row.pipelineName });
    setDeleteWorkflowDialogOpen(true);
  };

  const handleConfirmDeleteWorkflow = async () => {
    if (!workflowToDelete) return;

    try {
      await dispatch(deleteWorkflow(workflowToDelete.id)).unwrap();
      toast.success('Workflow deleted successfully');
      dispatch(fetchWorkflows());
      if (selectedProject) {
        dispatch(fetchFlowExecutionHistoryThunk({ limit: 1000 }));
      }
      setDeleteWorkflowDialogOpen(false);
      setWorkflowToDelete(null);
    } catch (error) {
      let errorMessage = 'Failed to delete workflow. Please try again or contact support if the issue persists.';
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.detail) {
        errorMessage = error.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      console.error('Delete workflow error:', error);
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleEdit = (row) => {
    if (row.workflowId) {
      navigate(`/parentworkflow/${row.workflowId}`);
    } else {
      toast.error('Workflow name not found');
    }
  };

  const renderActions = (row) => (
    <Box sx={{ display: 'flex', gap: '0.75rem' }}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleShare(row);
        }}
        sx={{
          bgcolor: 'white',
          borderRadius: '50%',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
        title="Share workflow"
      >
        <Box
          component="img"
          src={shareIcon}
          alt="Share"
          sx={{ width: 18, height: 18 }}
        />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => handleDelete(row, e)}
        sx={{
          bgcolor: 'white',
          borderRadius: '50%',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
        title="Delete workflow"
      >
        <Box
          component="img"
          src={deleteIcon}
          alt="Delete"
          sx={{ width: 18, height: 18 }}
        />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(row);
        }}
        sx={{
          bgcolor: 'white',
          borderRadius: '50%',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
        title="Edit workflow"
      >
        <Box
          component="img"
          src={pencilIcon}
          alt="Edit"
          sx={{ width: 18, height: 18 }}
        />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ flex: 1, bgcolor: '#fafbfd', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title="Workspace"
        showConnectorIcon={false}
        showSearchIcon={false}
        rightContent={<WorkspaceHeaderActions viewMode={viewMode} setViewMode={setViewMode} />}
        data-tour="workspace-header"
      />

      <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          variant={isSmDown ? 'scrollable' : 'standard'}
          scrollButtons={isSmDown ? 'auto' : false}
          allowScrollButtonsMobile
          data-tour="workspace-tabs"
          sx={{
            mb: '1.5rem',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: '"IBM Plex Sans", sans-serif',
                  fontSize: '0.9375rem',
                fontWeight: 400,
                lineHeight: 1.2,
                letterSpacing: '0%',
                verticalAlign: 'middle',
                color: '#3f3f3f',
                minHeight: 48,
              },
            '& .Mui-selected': { color: 'black !important', fontWeight: 500 },
            '& .MuiTabs-indicator': { height: 4, bgcolor: '#3e9663' },
          }}
        >
          <Tab
            icon={
              <Box
                component="img"
                src={recentIcon}
                alt="Recent"
                sx={{ width: 18, height: 18 }}
              />
            }
            iconPosition="start"
            label="Recent"
          />
          <Tab
            icon={
              <Box
                component="img"
                src={solarStarIcon}
                alt="Favourite"
                sx={{ width: 18, height: 18 }}
              />
            }
            iconPosition="start"
            label="Favourite"
          />
          <Tab
            icon={
              <Box
                component="img"
                src={borderAllIcon}
                alt="All Projects"
                sx={{ width: 18, height: 18 }}
              />
            }
            iconPosition="start"
            label="All Projects"
          />
        </Tabs>

        {viewMode === 'details' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: { xs: '0.5rem', sm: '0.75rem' },
              bgcolor: '#f9f9f9',
              border: '1px solid #e6e6e6',
              borderBottom: '2px solid #e6e6e6',
              borderRadius: '2px 2px 0 0',
              overflowX: { xs: 'auto', sm: 'visible' },
            }}
          >
            <Box sx={{ width: { xs: 20, sm: 24 }, mr: { xs: '0.5rem', sm: '1rem' } }} />
            <Typography
              sx={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                color: '#525252',
                width: { xs: '120px', sm: '200px' },
                minWidth: { xs: '120px', sm: '200px' },
                textTransform: 'uppercase',
              }}
            >
              Name
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                color: '#525252',
                flex: 1,
                mx: { xs: '0.5rem', sm: '1rem' },
                textTransform: 'uppercase',
                display: { xs: 'none', md: 'block' },
              }}
            >
              Description
            </Typography>
            <Box
              sx={{
                width: { xs: '100px', sm: '140px' },
                minWidth: { xs: '100px', sm: '140px' },
                maxWidth: { xs: '100px', sm: '140px' },
                textAlign: 'left',
                mr: { xs: '0.5rem', sm: '1rem' },
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  color: '#525252',
                  textTransform: 'uppercase',
                }}
              >
                Created
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '100px', sm: '140px' },
                minWidth: { xs: '100px', sm: '140px' },
                maxWidth: { xs: '100px', sm: '140px' },
                textAlign: 'left',
                mr: { xs: '0.5rem', sm: '1rem' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  color: '#525252',
                  textTransform: 'uppercase',
                }}
              >
                Modified
              </Typography>
            </Box>
            <Box 
              sx={{ 
                width: '14px',
                minWidth: '14px',
                maxWidth: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
            </Box>
          </Box>
        )}
        {viewMode === 'list' && (
          <Box
            sx={{
              mb: { xs: '2rem', sm: '2rem', md: '3rem' },
            }}
          >
            {renderTimeGroupedList()}
          </Box>
        )}
        {viewMode !== 'list' && (
          <Box
            data-tour="workspace-project-grid"
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden',
              mb: { xs: '2rem', sm: '2rem', md: '3rem' },
              ...(viewMode === 'details' && {
                border: '1px solid #e6e6e6',
                borderTop: 'none',
                borderRadius: '0 0 2px 2px',
                bgcolor: 'white',
              }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: viewMode === 'tiles' ? { xs: 'wrap', sm: 'wrap' } : viewMode === 'cards' ? 'nowrap' : 'nowrap',
                flexDirection: viewMode === 'details' ? 'column' : 'row',
                gap: viewMode === 'details' ? 0 : { xs: '0.75rem', sm: '1rem' },
                overflowX: viewMode === 'cards' ? 'auto' : viewMode === 'details' ? { xs: 'auto', sm: 'visible' } : 'visible',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                width: '100%',
                maxWidth: '100%',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: '2px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#d0d0d0',
                  borderRadius: '1px',
                  '&:hover': {
                    background: '#b0b0b0',
                  },
                },
              }}
            >
              {renderProjects()}
            </Box>
          </Box>
        )}
      </Box>

      {selectedProject && (
        <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: '2rem' }}>
          <Card
            data-tour="workspace-project-detail"
            sx={{
              borderLeft: '4px solid #3b9562',
              boxShadow: 'none',
              borderRadius: 0,
            }}
          >
            <Box sx={{ p: { xs: '1rem', sm: '1.5rem' } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: '0.75rem',
                  mb: '1rem',
                }}
              >
              <Box
                sx={{
                  width: 30,
                  height: 25.6,
                  borderRadius: 1,
                }}
              >
                <Box
                  component="img"
                  src={folderIcon2}
                  alt="Project overview folder"
                  sx={{ width: 30, height: 25.6 }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  lineHeight: 1.2,
                  letterSpacing: '0%',
                  color: '#000000',
                }}
              >
                {selectedProject ? `${selectedProject.name} Overview` : 'Select a Project'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem',
                mb: '1.5rem',
                ml: { xs: 0, sm: '3.25rem' },
                alignItems: 'center',
              }}
            >
              {selectedProject ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      fontSize: '0.8125rem',
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#525252',
                    }}
                  >
                    Created On {selectedProject.created_at ? dayjs(selectedProject.created_at).format('DD MMM') : 'N/A'}
                  </Typography>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#929292',
                      display: { xs: 'none', sm: 'inline-block' },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      fontSize: '0.8125rem',
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#525252',
                    }}
                  >
                    Last Modified {selectedProject.updated_at ? dayjs(selectedProject.updated_at).format('DD MMM') : 'N/A'}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: '0.8125rem',
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    color: '#525252',
                  }}
                >
                  No project selected
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row', md: 'row'},
                gap: { xs: '0.75rem', sm: '1rem' },
                p: '1rem',
                ml: { xs: 0, sm: '3.25rem' },
                background:
                  'linear-gradient(90.77deg, #EEF3FF -3.87%, rgba(238, 243, 255, 0) 173.05%)',
                borderRadius: '2px',
                boxShadow: '0px 0px 4px 0px #97979733',
                mb: '1.5rem',
                width: { xs: '100%', sm: 'fit-content' },
                maxWidth: '100%',
                justifyContent: { xs: 'space-between', sm: 'flex-start' },
              }}
            >
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={subcard1}
                    alt="Workflows"
                    sx={{ width: 16, height: 16 }}
                  />
                }
                label="Workflows"
                value={projectStats?.workflows?.toString() || '0'}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  opacity: 0.5,
                  mx: '0.5rem',
                  alignSelf: 'center',
                  height: 28,
                  display: { xs: 'none', sm: 'none', md: 'block' },
                }}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={subcard2}
                    alt="Transformations"
                    sx={{ width: 16, height: 16 }}
                  />
                }
                label="Transformations"
                value={projectStats?.transformations?.toString() || '0'}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  opacity: 0.5,
                  mx: '0.5rem',
                  alignSelf: 'center',
                  height: 28,
                  display: { xs: 'none', sm: 'none', md: 'block' },
                }}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={subcard3}
                    alt="Sources"
                    sx={{ width: 16, height: 16 }}
                  />
                }
                label="Sources"
                value={projectStats?.sources?.toString() || '0'}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  opacity: 0.5,
                  mx: '0.5rem',
                  alignSelf: 'center',
                  height: 28,
                  display: { xs: 'none', sm: 'none', md: 'block' },
                }}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={subcard4}
                    alt="Destinations"
                    sx={{ width: 16, height: 16 }}
                  />
                }
                label="Destinations"
                value={projectStats?.destinations?.toString() || '0'}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  opacity: 0.5,
                  mx: '0.5rem',
                  alignSelf: 'center',
                  height: 28,
                  display: { xs: 'none', sm: 'none', md: 'block' },
                }}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={subcard5}
                    alt="Last Run"
                    sx={{ width: 16, height: 16 }}
                  />
                }
                label="Last Run"
                value={projectStats?.lastRun || 'No runs yet'}
              />
            </Box>

            <Box sx={{ ml: { xs: 0, sm: '3.25rem' } }}>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  letterSpacing: '0%',
                  color: '#383838',
                  verticalAlign: 'middle',
                  mb: '1.25rem',
                }}
              >
                Flow Execution History
              </Typography>
              
              <Box 
                data-tour="workspace-workflows-table"
                sx={{ 
                width: '100%', 
                overflowX: 'auto',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: '2px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#d0d0d0',
                  borderRadius: '1px',
                  '&:hover': {
                    background: '#b0b0b0',
                  },
                },
              }}>
                <DataTable
                  columns={columns}
                  data={projectExecutions}
                  loading={flowExecutionLoading}
                  showSerialNumber={true}
                  showActions={true}
                  renderActions={renderActions}
                  minWidth={720}
                  containerSx={{
                    border: '0.5px solid #d3d3d4',
                    borderRadius: 0,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      )}

      <PrimaryDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setProjectToDelete(null);
        }}
        onSave={handleConfirmDelete}
        title="Delete Project"
        subtitle={projectToDelete ? `Are you sure you want to delete project "${projectToDelete.name}"? This action cannot be undone.` : ''}
        fields={[]}
        submitButtonText="Delete"
        cancelButtonText="Cancel"
        submitButtonVariant="danger"
        maxWidth="sm"
      />

      <PrimaryDialog
        open={deleteWorkflowDialogOpen}
        onClose={() => {
          setDeleteWorkflowDialogOpen(false);
          setWorkflowToDelete(null);
        }}
        onSave={handleConfirmDeleteWorkflow}
        title="Delete Workflow"
        subtitle={workflowToDelete ? `Are you sure you want to delete workflow "${workflowToDelete.name}"? This action cannot be undone.` : ''}
        fields={[]}
        submitButtonText="Delete"
        cancelButtonText="Cancel"
        submitButtonVariant="danger"
        maxWidth="sm"
      />
      <OnboardingTour />
    </Box>
  );
}

function StatItem({ icon, label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: '0.375rem', alignItems: 'flex-start' }}>
      <Box sx={{ color: '#525252', fontSize: '0.875rem', mt: '0.125rem' }}>{icon}</Box>
      <Box>
        <Typography
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.2,
            letterSpacing: '0%',
            color: '#525252',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '0.8125rem',
            lineHeight: 1.2,
            letterSpacing: '0%',
            color: '#000000',
            mt: '0.25rem',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

StatItem.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};
